if (typeof CoffeeSubscriptionWidget !== 'function') {

  class CoffeeSubscriptionWidget extends HTMLElement {

    constructor() {
      super();

      this.uiRoot          = this.querySelector('[data-coffee-subscription-ui]');
      this.nativeRoot      = this.querySelector('[data-coffee-subscription-native]');
      this.spGroupsNode    = this.querySelector('[data-coffee-subscription-selling-plan-groups]');
      this.productPage     = this.closest('product-page');
      this.productVariants = this.productPage?.querySelector('product-variants[data-main-product-variants]') || null;
      this.productForm     = document.getElementById(this.dataset.productFormId) || null;
      this.spInput         = this.productForm?.querySelector('[data-coffee-subscription-selling-plan-input]') || null;
      this.spGroups        = this.parseSellingPlanGroups();

      this.options         = [];
      this._type           = 'one-time';
      this._expanded       = true;
      this._userInteracted = false;
      this._freqValue      = '';
      this._initialized    = false;
      this._paused         = false;
      this._pending        = false;

      this.queueRefresh    = this.queueRefresh.bind(this);
      this.onVariantChange = this.onVariantChange.bind(this);
    }

    /* ── Lifecycle ─────────────────────────────────────────────────── */

    connectedCallback() {
      if (!this.uiRoot || !this.nativeRoot || this.hasAttribute('data-initialized')) return;
      this.setAttribute('data-initialized', 'true');

      this._observer = new MutationObserver(() => {
        if (!this._paused) this.queueRefresh();
      });
      this._observer.observe(this.nativeRoot, { childList: true, subtree: true });

      if (this.productVariants) {
        this.productVariants.addEventListener('VARIANT_CHANGE', this.onVariantChange);
      }

      this.queueRefresh();
      [300, 800, 1600].forEach(delay => window.setTimeout(this.queueRefresh, delay));
    }

    disconnectedCallback() {
      this._observer?.disconnect();
      if (this.productVariants) {
        this.productVariants.removeEventListener('VARIANT_CHANGE', this.onVariantChange);
      }
    }

    onVariantChange() {
      this._initialized = false;
      window.setTimeout(this.queueRefresh, 200);
    }

    queueRefresh() {
      if (this._pending) return;
      this._pending = true;
      window.requestAnimationFrame(() => {
        this._pending = false;
        this.refresh();
      });
    }

    /* ── Main refresh ──────────────────────────────────────────────── */

    refresh() {
      const wrappers = this.getOptionWrappers();
      if (wrappers.length < 2) {
        this.classList.remove('is-enhanced');
        this.uiRoot.innerHTML = '';
        return;
      }

      this.options = wrappers
        .map((w, i) => this.buildOptionData(w, i))
        .filter(Boolean);

      const subOption     = this.options.find(o => o.type === 'subscription');
      const oneTimeOption = this.options.find(o => o.type === 'one-time');

      if (!subOption || !oneTimeOption) {
        this.classList.remove('is-enhanced');
        this.uiRoot.innerHTML = '';
        return;
      }

      const nativeSel   = this.getNativeFrequencySelect();
      const freqOptions = this.buildFrequencyOptions(nativeSel);

      if (!this._initialized) {
        this._initialized = true;
        if (!this._userInteracted) {
          this._type      = 'one-time';
          this._expanded  = true;
          this._freqValue = '';
        }

        const nativeOption = this.options.find(o => o.type === this._type) || oneTimeOption;
        this._paused = true;
        nativeOption.input.checked = true;
        const labelEl = this.nativeRoot.querySelector(`label[for="${nativeOption.input.id}"]`);
        if (labelEl) labelEl.click();
        else nativeOption.input.click();
        window.setTimeout(() => { this._paused = false; }, 400);
      } else if (!this._paused && this._type === 'subscription' && !this._freqValue) {
        if (nativeSel) {
          const nativeVal = String(nativeSel.value || '');
          if (nativeVal) this._freqValue = nativeVal;
        }
      }

      this.renderUI(nativeSel, freqOptions);
      this.syncSellingPlanInput(nativeSel, freqOptions);
      this.classList.add('is-enhanced');
    }

    /* ── Data helpers ──────────────────────────────────────────────── */

    getOptionWrappers() {
      let w = Array.from(this.nativeRoot.querySelectorAll('.shopify_subscriptions_app_block_label'));
      if (w.length >= 2) return w.filter(el => el.querySelector('input[type="radio"]'));

      const radios = Array.from(this.nativeRoot.querySelectorAll('input[type="radio"]'));
      w = radios.map(r => r.closest('label, .shopify_subscriptions_app_block_label, li, div')).filter(Boolean);
      return Array.from(new Set(w)).filter(el => el.querySelector('input[type="radio"]'));
    }

    getNativeFrequencySelect() {
      return Array.from(this.nativeRoot.querySelectorAll('select'))
        .find(s => s.options && s.options.length > 0) || null;
    }

    buildFrequencyOptions(nativeSel) {
      if (nativeSel) {
        const opts = Array.from(nativeSel.options)
          .map(o => ({ value: this.str(o.value), label: this.formatFreqLabel(this.str(o.textContent)) }))
          .filter(o => o.value !== '');
        if (opts.length > 0) return opts;
      }
      return this.getFallbackFrequencyOptions();
    }

    parseSellingPlanGroups() {
      if (!this.spGroupsNode) return [];
      try {
        const parsed = JSON.parse(this.spGroupsNode.textContent || '[]');
        return Array.isArray(parsed) ? parsed : [];
      } catch (_) { return []; }
    }

    getFallbackFrequencyOptions() {
      if (!Array.isArray(this.spGroups) || this.spGroups.length === 0) return [];

      const seen    = new Set();
      const options = [];

      this.spGroups.forEach(group => {
        if (!Array.isArray(group.selling_plans)) return;
        group.selling_plans.forEach(plan => {
          if (!plan || plan.id == null) return;
          const value = String(plan.id);
          if (seen.has(value)) return;
          seen.add(value);

          let raw = '';
          if (Array.isArray(plan.options) && plan.options.length > 0) raw = this.str(plan.options[0]);
          if (!raw && plan.name) raw = this.str(plan.name);
          if (!raw) raw = `Plan ${value}`;

          options.push({ value, label: this.formatFreqLabel(raw) });
        });
      });

      return options;
    }

    // Transforms Seal frequency labels into localised sentences using Shopify.locale
    formatFreqLabel(raw) {
      const s    = this.str(raw).toLowerCase();
      const lang = (window.Shopify && Shopify.locale ? Shopify.locale : 'fr').split('-')[0];

      // ── Frequency templates per language ───────────────────────────
      const T = {
        fr: {
          every_week:    'Livraison chaque semaine',
          every_n_weeks: n => `Livraison toutes les ${n} semaines`,
          every_month:   'Livraison chaque mois',
          every_n_months: n => `Livraison tous les ${n} mois`,
          every_year:    'Livraison chaque année',
          prefix:        'Livraison ',
          prefix_re:     /^livraison/i,
        },
        en: {
          every_week:    'Delivery every week',
          every_n_weeks: n => `Delivery every ${n} weeks`,
          every_month:   'Delivery every month',
          every_n_months: n => `Delivery every ${n} months`,
          every_year:    'Delivery every year',
          prefix:        'Delivery ',
          prefix_re:     /^delivery/i,
        },
        de: {
          every_week:    'Lieferung jede Woche',
          every_n_weeks: n => `Lieferung alle ${n} Wochen`,
          every_month:   'Lieferung jeden Monat',
          every_n_months: n => `Lieferung alle ${n} Monate`,
          every_year:    'Lieferung jedes Jahr',
          prefix:        'Lieferung ',
          prefix_re:     /^lieferung/i,
        },
        it: {
          every_week:    'Consegna ogni settimana',
          every_n_weeks: n => `Consegna ogni ${n} settimane`,
          every_month:   'Consegna ogni mese',
          every_n_months: n => `Consegna ogni ${n} mesi`,
          every_year:    'Consegna ogni anno',
          prefix:        'Consegna ',
          prefix_re:     /^consegna/i,
        },
      };

      const t = T[lang] || T.fr;

      // ── Detect unit and optional number (language-agnostic patterns) ─
      const weekRe  = /\b(week|semaine|woche|settimana)s?\b/;
      const monthRe = /\b(month|mois|monat|mese)s?\b/;
      const yearRe  = /\b(year|ann[eé]e|jahr|anno)s?\b/;
      const numMatch = s.match(/\b([2-9]|1[0-2])\b/);
      const n = numMatch ? parseInt(numMatch[1], 10) : 1;

      if (weekRe.test(s))  return n > 1 ? t.every_n_weeks(n)  : t.every_week;
      if (monthRe.test(s)) return n > 1 ? t.every_n_months(n) : t.every_month;
      if (yearRe.test(s))  return t.every_year;

      // Fallback: keep raw label, prefix if needed
      return t.prefix_re.test(raw) ? this.str(raw) : t.prefix + this.str(raw);
    }

    buildOptionData(wrapper, index) {
      const input = wrapper.querySelector('input[type="radio"]');
      if (!input) return null;

      const labelEl = wrapper.querySelector('label') || wrapper;
      const text    = this.str(labelEl.textContent);
      const type    = this.detectType(text, wrapper, index);

      return { type, input };
    }

    detectType(text, wrapper, index) {
      const n = text.toLowerCase();
      if (/abon|subscr|livrai|recurr|deliver|plan/.test(n))        return 'subscription';
      if (/achat|ponctuel|unique|one[\s-]?time/.test(n))           return 'one-time';
      if (wrapper.querySelector('ul, select, input[name="selling_plan"]')) return 'subscription';
      return index === 0 ? 'one-time' : 'subscription';
    }

    str(value) {
      if (value == null) return '';
      return String(value).replace(/\s+/g, ' ').trim();
    }

    esc(value) {
      return this.str(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }

    /* ── Selling plan input sync ───────────────────────────────────── */

    syncSellingPlanInput(nativeSel, freqOptions) {
      if (!this.spInput) return;

      if (this._type !== 'subscription' || !this._freqValue) {
        this.spInput.value    = '';
        this.spInput.disabled = true;
        return;
      }

      const value = this._freqValue;

      if (nativeSel && value && nativeSel.value !== value) {
        nativeSel.value = value;
      }

      this.spInput.value    = value;
      this.spInput.disabled = !value;
    }

    /* ── Rendering ─────────────────────────────────────────────────── */

    renderUI(nativeSel, freqOptions) {
      const isSelected = this._type === 'subscription' && Boolean(this._freqValue);
      const isOpen     = this._expanded || isSelected;

      const ctaLabel   = this.dataset.ctaLabel     || 'Je m\u2019abonne et \u00e9conomise 10\u00a0%';
      const placeholderLabel = this.placeholderLabel();
      const b1         = this.dataset.benefit1     || '10\u00a0% de r\u00e9duction';
      const b2         = this.dataset.benefit2     || 'Livraison gratuite dès 30 CHF';
      const b3         = this.dataset.benefit3     || 'Pause ou annulation \u00e0 tout moment';
      const ariaGroup  = this.dataset.ariaGroup    || "Options d'achat";
      const ariaFreq   = this.dataset.ariaFrequency || 'Fr\u00e9quence de livraison';
      const ariaDismiss = this.dataset.ariaDismiss || "Annuler l'abonnement";
      const iconMode   = this.dataset.benefitIcon  || 'check';
      const iconChar  = iconMode === 'check' ? '✓' : iconMode === 'dash' ? '—' : iconMode === 'bullet' ? '●' : null;
      const iconHtml  = iconChar
        ? `<span class="coffee-subscription-benefit__check" aria-hidden="true">${iconChar}</span>`
        : '';

      this.uiRoot.innerHTML = `
        <div class="coffee-subscription-ui${isOpen ? ' is-subscription-active' : ''}"
             role="group"
             aria-label="${this.esc(ariaGroup)}">
          <button
            class="coffee-subscription-cta${isSelected ? ' is-active' : ''}"
            data-coffee-subscription-toggle
            type="button"
            aria-pressed="${isSelected ? 'true' : 'false'}"
          >${this.esc(ctaLabel)}</button>
          <div class="coffee-subscription-freq-inline" aria-hidden="${isOpen ? 'false' : 'true'}">
            <select
              class="coffee-subscription-freq-select${!this._freqValue ? ' is-placeholder' : ''}"
              data-coffee-subscription-frequency
              aria-label="${this.esc(ariaFreq)}"
            ><option value=""${!this._freqValue ? ' selected' : ''}>${this.esc(placeholderLabel)}</option>${freqOptions.map(o => `<option value="${this.esc(o.value)}"${o.value === this._freqValue ? ' selected' : ''}>${this.esc(o.label)}</option>`).join('')}</select>
            <button
              class="coffee-subscription-dismiss"
              data-coffee-subscription-dismiss
              type="button"
              aria-label="${this.esc(ariaDismiss)}"
            >&times;</button>
          </div>
        </div>
        <div class="coffee-subscription-benefits coffee-subscription-benefits--icon-${iconMode}" aria-hidden="${isOpen ? 'false' : 'true'}">
          <span class="coffee-subscription-benefit">${iconHtml}${this.esc(b1)}</span>
          <span class="coffee-subscription-benefit">${iconHtml}${this.esc(b2)}</span>
          <span class="coffee-subscription-benefit">${iconHtml}${this.esc(b3)}</span>
        </div>
      `;

      this.uiRoot.querySelector('[data-coffee-subscription-toggle]')
        .addEventListener('click', () => {
          this.setType(
            this._freqValue ? 'subscription' : 'one-time',
            nativeSel,
            freqOptions,
            { expanded: true }
          );
        });

      this.uiRoot.querySelector('[data-coffee-subscription-dismiss]')
        .addEventListener('click', () => {
          this._freqValue = '';
          this.setType('one-time', nativeSel, freqOptions, { expanded: false });
        });

      const freqSel = this.uiRoot.querySelector('[data-coffee-subscription-frequency]');
      if (freqSel) {
        freqSel.addEventListener('change', e => {
          this._freqValue = e.currentTarget.value;
          this.setType(
            this._freqValue ? 'subscription' : 'one-time',
            nativeSel,
            freqOptions,
            { expanded: true }
          );
        });
      }
    }

    /* ── State toggle (no full re-render) ──────────────────────────── */

    setType(type, nativeSel, freqOptions, options = {}) {
      const nextExpanded = Object.prototype.hasOwnProperty.call(options, 'expanded')
        ? Boolean(options.expanded)
        : type === 'subscription';

      if (this._type === type && this._expanded === nextExpanded) {
        this.syncSellingPlanInput(nativeSel, freqOptions);
        this.syncFrequencySelect();
        return;
      }

      this._type           = type;
      this._expanded       = nextExpanded;
      this._userInteracted = true;

      const option = this.options.find(o => o.type === type);
      if (option) {
        this._paused = true;
        option.input.checked = true;
        const labelEl = this.nativeRoot.querySelector(`label[for="${option.input.id}"]`);
        if (labelEl) labelEl.click();
        else option.input.click();
        option.input.dispatchEvent(new Event('change', { bubbles: true }));
        window.setTimeout(() => { this._paused = false; }, 350);
      }

      this.syncSellingPlanInput(nativeSel, freqOptions);

      const isSelected = type === 'subscription' && Boolean(this._freqValue);
      const isOpen     = this._expanded;
      const uiEl       = this.uiRoot.querySelector('.coffee-subscription-ui');
      const ctaEl      = this.uiRoot.querySelector('[data-coffee-subscription-toggle]');
      const freqEl     = this.uiRoot.querySelector('.coffee-subscription-freq-inline');
      const benefitsEl = this.uiRoot.querySelector('.coffee-subscription-benefits');

      if (uiEl)       uiEl.classList.toggle('is-subscription-active', isOpen);
      if (ctaEl) {
        ctaEl.classList.toggle('is-active', isSelected);
        ctaEl.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
      }
      if (freqEl)     freqEl.setAttribute('aria-hidden', isOpen ? 'false' : 'true');
      if (benefitsEl) benefitsEl.setAttribute('aria-hidden', isOpen ? 'false' : 'true');
      this.syncFrequencySelect();
    }

    placeholderLabel() {
      if (this.dataset.placeholderLabel) return this.dataset.placeholderLabel;
      const lang = (window.Shopify && Shopify.locale ? Shopify.locale : 'fr').split('-')[0];
      const labels = {
        fr: 'Choisissez votre formule',
        en: 'Choose your plan',
        de: 'Wählen Sie Ihr Abo',
        it: 'Scegli la formula',
      };
      return labels[lang] || labels.fr;
    }

    syncFrequencySelect() {
      const freqSel = this.uiRoot.querySelector('[data-coffee-subscription-frequency]');
      if (!freqSel) return;
      freqSel.value = this._freqValue || '';
      freqSel.classList.toggle('is-placeholder', !this._freqValue);
    }

  }

  if (typeof customElements.get('coffee-subscription-widget') === 'undefined') {
    customElements.define('coffee-subscription-widget', CoffeeSubscriptionWidget);
  }

}
